import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'
import { CheckCircleIcon, AcademicCapIcon, UserGroupIcon, ClockIcon, ArrowRightIcon, PlusIcon } from '@heroicons/react/24/outline'

interface LessonPostSuccessState {
  date: string
  subjectsCount: number
  studentsNotified: number
  subjects?: string[]
  hasImages?: boolean
}

export const LessonPostSuccessPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LessonPostSuccessState

  // If no state provided, redirect back
  useEffect(() => {
    if (!state?.date) {
      navigate('/admin/lessons', { replace: true })
    }
  }, [state, navigate])

  if (!state?.date) {
    return null // Will redirect in useEffect
  }

  const formatThaiDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const thaiYear = date.getFullYear() + 543
    const months = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ]
    const days = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']
    
    return `วัน${days[date.getDay()]}ที่ ${date.getDate()} ${months[date.getMonth()]} ${thaiYear}`
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="โพสต์บทเรียนสำเร็จ"
        showBackButton={false}
      />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIcon className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            โพสต์บทเรียนสำเร็จ!
          </h1>
          <p className="text-gray-600">
            บทเรียนได้ถูกส่งไปยังผู้ปกครองแล้ว
          </p>
        </div>

        {/* Lesson Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">📚 รายละเอียดบทเรียน</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">วันที่:</span>
              <span className="font-medium">{formatThaiDate(state.date)}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-600">จำนวนวิชา:</span>
              <span className="font-medium text-blue-600">{state.subjectsCount} วิชา</span>
            </div>

            {state.subjects && state.subjects.length > 0 && (
              <div>
                <span className="text-gray-600 block mb-2">วิชาที่สอน:</span>
                <div className="flex flex-wrap gap-2">
                  {state.subjects.map((subject, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                      {subject}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <span className="text-gray-600">ส่งถึงผู้ปกครอง:</span>
              <span className="font-medium text-green-600">{state.studentsNotified} คน</span>
            </div>

            {state.hasImages && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">รูปภาพแนบ:</span>
                <span className="font-medium text-purple-600">✓ มีแนบ</span>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <span className="text-gray-600">เวลาที่โพสต์:</span>
              <span className="font-medium">
                {new Date().toLocaleString('th-TH', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })} น.
              </span>
            </div>
          </div>
        </div>

        {/* Notification Status */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <UserGroupIcon className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-green-800 mb-1">แจ้งเตือนผู้ปกครองแล้ว</h3>
              <p className="text-sm text-green-700 mb-2">
                ส่งการแจ้งเตือนผ่าน LINE ไปยังผู้ปกครอง {state.studentsNotified} คนเรียบร้อยแล้ว
              </p>
              <p className="text-xs text-green-600">
                💡 ผู้ปกครองสามารถดูรายละเอียดบทเรียนได้ในแอปพลิเคชัน
              </p>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <h3 className="font-medium text-blue-900 mb-3 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2" />
            📋 สิ่งที่ต้องทำต่อไป
          </h3>
          <div className="space-y-2 text-sm text-blue-800">
            <div className="flex items-center">
              <span className="w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 flex-shrink-0">1</span>
              <span>ตรวจสอบว่านักเรียนได้รับการบ้านครบถ้วน</span>
            </div>
            <div className="flex items-center">
              <span className="w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 flex-shrink-0">2</span>
              <span>เตรียมเนื้อหาสำหรับการเรียนครั้งต่อไป</span>
            </div>
            <div className="flex items-center">
              <span className="w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 flex-shrink-0">3</span>
              <span>บันทึกค่าใช้จ่าย (ถ้ามี)</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/admin/lessons')}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <AcademicCapIcon className="h-5 w-5 mr-2" />
            ดูบทเรียนทั้งหมด
          </button>
          
          <button
            onClick={() => navigate('/admin/lessons/post')}
            className="w-full bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            โพสต์บทเรียนใหม่
          </button>
          
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
          >
            <ArrowRightIcon className="h-5 w-5 mr-2 rotate-180" />
            กลับไปหน้าหลัก
          </button>
        </div>

        {/* Quick Stats */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 mb-3">📊 สถิติวันนี้</h3>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">1</div>
              <div className="text-sm text-gray-600">บทเรียนที่โพสต์</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{state.studentsNotified}</div>
              <div className="text-sm text-gray-600">ผู้ปกครองที่ได้รับ</div>
            </div>
          </div>
        </div>
      </div>

      <TabNavigation activeTab="lessons" />
    </div>
  )
}